import Reveal from "@/components/base/Reveal";

export default function Philosophy() {
  const thumbs = [
    {
      src: "https://readdy.ai/api/search-image?query=Extreme%20minimalist%20macro%20detail%20of%20matte%20black%20curved%20aluminum%20surface%20with%20a%20single%20soft%20highlight%20arc%2C%20monochrome%20warm%20gray%20background%2C%20editorial%20industrial%20design%20texture%20study%2C%20refined%20negative%20space%2C%20cinematic%20studio%20lighting%2C%20high%20end%20product%20photography%20detail&width=600&height=600&seq=topbud-philo-thumb-01&orientation=squarish",
      alt: "Surface texture study",
      caption: "Shell",
    },
    {
      src: "https://readdy.ai/api/search-image?query=Close%20up%20detail%20of%20a%20single%20deep%20dark%20circular%20glass%20lens%20eye%20on%20a%20matte%20black%20AI%20device%2C%20subtle%20reflection%2C%20soft%20studio%20lighting%2C%20monochrome%20muted%20background%2C%20editorial%20product%20photography%2C%20minimalist%20futuristic%20aesthetic&width=600&height=600&seq=topbud-philo-thumb-02&orientation=squarish",
      alt: "Lens detail",
      caption: "Lens",
    },
  ];

  return (
    <section
      id="philosophy"
      className="w-full px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 border-t border-background-200"
    >
      {/* Section header row */}
      <Reveal y={30}>
        <div className="flex items-end justify-between mb-16 md:mb-24">
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
            <span>01 — Philosophy</span>
          </p>
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500 hidden md:block">
            <span>A quiet manifesto</span>
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-14 items-stretch">
        {/* LEFT — Media block: big image + thumb row */}
        <div className="flex flex-col gap-4">
          <Reveal y={60} duration={1000}>
            <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-100 border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Extreme%20minimalist%20still%20life%20of%20a%20single%20matte%20black%20rounded%20AI%20device%20resting%20on%20folded%20soft%20warm%20paper%20surface%2C%20dramatic%20single%20side%20light%20from%20left%2C%20long%20soft%20shadow%2C%20monochrome%20muted%20warm%20gray%20background%2C%20editorial%20fine%20art%20industrial%20design%20photography%2C%20refined%20negative%20space%2C%20high%20end%20product%20detail&width=1200&height=1500&seq=topbud-philo-main&orientation=portrait"
                alt="Topbud form study"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute left-4 bottom-4 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50">
                  Study N°01 / Form
                </span>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 gap-4">
            {thumbs.map((t, i) => (
              <Reveal key={t.caption} delay={200 + i * 140} y={40} duration={900}>
                <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-background-100 border border-background-200">
                  <img
                    src={t.src}
                    alt={t.alt}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  />
                  <span className="absolute left-3 bottom-3 font-label text-[10px] tracking-[0.22em] uppercase text-background-50">
                    {t.caption}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* RIGHT — Editorial text column */}
        <div className="flex flex-col justify-between h-full">
          <div className="max-w-md">
            <Reveal delay={120} y={40}>
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                <span>Manifesto</span>
              </p>
            </Reveal>
            <Reveal delay={220} y={40}>
              <h2 className="mt-6 font-heading font-semibold text-[48px] md:text-[68px] leading-[0.95] tracking-[-0.03em] text-foreground-950">
                Quiet by <br />
                <span className="italic font-normal text-foreground-700">design.</span>
              </h2>
            </Reveal>

            <Reveal delay={340} y={40}>
              <p className="mt-10 text-[13px] leading-[1.75] text-foreground-700 text-justify">
                One shape. One surface. One voice. Every detail is stripped back
                until only intention remains — a companion that listens more than
                it speaks. We began with a question — what if a device could
                belong to the room instead of demanding it? Every curve was
                sanded, every seam removed, every light dimmed until only calm
                remained.
              </p>
            </Reveal>

            <Reveal delay={460} y={40}>
              <p className="mt-6 text-[13px] leading-[1.75] text-foreground-700 text-justify">
                We do not build for the next feature. We build for the next
                decade — an object that ages quietly on a shelf, remembers only
                what you allow, and asks for nothing in return.
              </p>
            </Reveal>

            {/* Signature row */}
            <Reveal delay={580} y={30}>
              <div className="mt-10 flex items-center gap-4">
                <span className="h-px w-10 bg-foreground-400" />
                <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-600">
                  — Design Office, 2025
                </span>
              </div>
            </Reveal>
          </div>

          {/* Bottom metadata row */}
          <Reveal delay={680} y={30}>
            <div className="mt-16 grid grid-cols-3 gap-6 border-t border-background-200 pt-6">
              {[
                { k: "Iterations", v: "128" },
                { k: "Prototypes", v: "34" },
                { k: "Years", v: "04" },
              ].map((m) => (
                <div key={m.k}>
                  <p className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500">
                    {m.k}
                  </p>
                  <p className="mt-2 font-heading text-[28px] leading-none tracking-[-0.02em] text-foreground-950">
                    {m.v}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}